// jmix-8: locale-aware formatting for the foster portal (parity with the legacy Vaadin views, which
// used a Dutch locale: dd-MM-yyyy short dates, "d MMMM yyyy" long dates, a decimal-comma for weights).
// Pure helpers — the screens pass the current locale from Refine's useGetLocale.

import type { Locale } from "../../i18n/messages";

function intlLocale(locale: Locale | undefined): string {
  return locale === "en" ? "en-GB" : "nl-NL";
}

/** A parsed Date, or null for a null/blank/invalid ISO string (so callers can render a "—" fallback). */
function parse(iso: string | null | undefined): Date | null {
  if (!iso) {
    return null;
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "01-09-2026" (nl) — the short numeric date the animal list / DOB used. */
export function formatDateShort(iso: string | null | undefined, locale?: Locale): string {
  const d = parse(iso);
  return d
    ? new Intl.DateTimeFormat(intlLocale(locale), {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(d)
    : "—";
}

/** "1 september 2026" (nl) — the long date absences/dashboard used. */
export function formatDateLong(iso: string | null | undefined, locale?: Locale): string {
  const d = parse(iso);
  return d
    ? new Intl.DateTimeFormat(intlLocale(locale), {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(d)
    : "—";
}

/** "10-06-2026 10:00" — a recent-update timestamp. */
export function formatDateTime(iso: string | null | undefined, locale?: Locale): string {
  const d = parse(iso);
  return d
    ? new Intl.DateTimeFormat(intlLocale(locale), {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d)
    : "—";
}

/** The time-of-day part, "HH:mm" — used by the dashboard relative-time line ("Vandaag, 10:00"). */
export function formatTime(iso: string | null | undefined, locale?: Locale): string {
  const d = parse(iso);
  return d
    ? new Intl.DateTimeFormat(intlLocale(locale), {
        hour: "2-digit",
        minute: "2-digit",
      }).format(d)
    : "—";
}

/**
 * Calendar-day delta between an instant and today (local midnight to local midnight): 0 = today,
 * 1 = yesterday, >1 otherwise. Mirrors the legacy DashboardView.formatRelativeTime branch logic.
 */
export function dayDelta(iso: string | null | undefined): number | null {
  const d = parse(iso);
  if (!d) {
    return null;
  }
  const at = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today.getTime() - at.getTime()) / 86_400_000);
}

/** "12,3" (nl decimal comma) — a weight in kg, one fraction digit. */
export function formatWeight(kg: number | null | undefined, locale?: Locale): string {
  if (kg == null || Number.isNaN(kg)) {
    return "—";
  }
  return new Intl.NumberFormat(intlLocale(locale), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(kg);
}
